import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { DollarSign, Receipt, Clock, RefreshCw, Check, Loader2, AlertCircle } from "lucide-react";

export default function Billing() {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    const [s, i] = await Promise.all([api.get("/admin/stats"), api.get("/admin/invoices")]);
    setStats(s.data); setInvoices(i.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/invoices/generate");
      toast.success(`${data.created} factura(s) generadas vía Factura Directa (simulado)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const markPaid = async (id) => { await api.patch(`/admin/invoices/${id}/pay`); toast.success("Factura marcada como pagada"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Facturación</h1>
          <p className="text-slate-500 text-sm mt-1">Facturas recurrentes del SaaS por empresa cliente.</p>
        </div>
        <Button onClick={generate} disabled={generating} className="brand-btn" data-testid="generate-invoices-btn">
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-2" /> Generar facturas del mes</>}
        </Button>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="facturadirecta-notice">
        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <span className="font-semibold">Integración Factura Directa: SIMULADA.</span> Las facturas se generan en modo mock.
          Añade tu API Key en <code className="bg-amber-100 px-1 rounded">FACTURADIRECTA_API_KEY</code> para activar la facturación real automatizada.
        </div>
      </div>

      {!stats ? null : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testId="bill-mrr" label="MRR" value={`${stats.mrr}€`} icon={DollarSign} accent />
          <KpiCard testId="bill-revenue" label="Facturado (cobrado)" value={`${stats.total_revenue}€`} icon={Receipt} />
          <KpiCard testId="bill-pending" label="Facturas pendientes" value={stats.pending_invoices} icon={Clock} />
          <KpiCard testId="bill-companies" label="Empresas activas" value={stats.active_companies} icon={Receipt} />
        </div>
      )}

      <Card className="border-slate-200 shadow-none overflow-hidden">
        {loading ? <div className="h-48 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div> : (
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead>Referencia</TableHead><TableHead>Empresa</TableHead><TableHead>Periodo</TableHead>
              <TableHead>Base</TableHead><TableHead>Total (IVA)</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id} className="group" data-testid={`invoice-row-${inv.id}`}>
                  <TableCell><code className="text-xs text-slate-500">{inv.external_ref}</code></TableCell>
                  <TableCell className="font-medium text-slate-800">{inv.company_name}</TableCell>
                  <TableCell className="text-slate-600">{inv.period}</TableCell>
                  <TableCell className="text-slate-600">{inv.base}€</TableCell>
                  <TableCell className="font-semibold text-slate-800">{inv.amount}€</TableCell>
                  <TableCell><Badge className={`border-0 ${inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{inv.status === "paid" ? "Pagada" : "Pendiente"}</Badge></TableCell>
                  <TableCell className="text-right">
                    {inv.status !== "paid" && <Button size="sm" variant="outline" onClick={() => markPaid(inv.id)} data-testid={`pay-${inv.id}`}><Check className="h-4 w-4 mr-1" /> Marcar pagada</Button>}
                  </TableCell>
                </TableRow>
              ))}
              {!invoices.length && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No hay facturas. Pulsa "Generar facturas del mes".</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
